import { useCallback } from 'react';
import moment from 'moment';

export const DEFAULT_ORDER_DATE_DAYS = 30;
export const DEFAULT_ORDER_DATE_DAYS60 = 59;
export const REVOKED_ORDER_DATE_DAYS = 59;
export const ORDER_DATE_ALERT_FLAG = 'orderDateAlert';

const ORDER_DATE_MESSAGE_ATTR = 'data-order-date-message';
const SHAKE_RETRY_MS = 50;
const SHAKE_MAX_RETRIES = 12;

/** Default from/to when entering an order tab (module-level date vars persist otherwise). */
export function resetOrderDateFilter(days = DEFAULT_ORDER_DATE_DAYS) {
  return {
    from: moment().subtract(days, 'days').format('YYYY-MM-DD'),
    to: moment().format('YYYY-MM-DD'),
  };
}

export function resetOrderDateFilter60Day(days = DEFAULT_ORDER_DATE_DAYS60) {
  return {
    from: moment().subtract(days, 'days').format('YYYY-MM-DD'),
    to: moment().format('YYYY-MM-DD'),
  };
}

/** Keep from <= to when the user changes either date picker. */
export function resolveOrderDateRange(type, value, from, to) {
  if (type === 'from') {
    const nextFrom = value || '';
    let nextTo = to || '';
    if (nextFrom && nextTo && nextFrom > nextTo) {
      nextTo = nextFrom;
    }
    return { from: nextFrom, to: nextTo };
  }
  const nextTo = value || '';
  let nextFrom = from || '';
  if (nextFrom && nextTo && nextTo < nextFrom) {
    nextFrom = nextTo;
  }
  return { from: nextFrom, to: nextTo };
}

let lastOrderDateAlertMsg = null;
let lastOrderDateAlertRef = null;
let cachedOrderDateAlertEl = null;
let shakeRetryTimeoutId = null;

const escapeMessageAttr = (message) => {
  if (typeof CSS !== 'undefined' && CSS.escape) {
    return CSS.escape(message);
  }
  return message.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
};

const findOrderDateAlertElement = (message) => {
  if (
    cachedOrderDateAlertEl &&
    document.body.contains(cachedOrderDateAlertEl) &&
    cachedOrderDateAlertEl.getAttribute(ORDER_DATE_MESSAGE_ATTR) === message
  ) {
    return cachedOrderDateAlertEl;
  }

  const el = document.querySelector(
    `[${ORDER_DATE_MESSAGE_ATTR}="${escapeMessageAttr(message)}"]`
  );
  if (el) {
    return el;
  }

  // Fallback for toasts shown before the wrapper existed in the same session.
  const spans = document.querySelectorAll('#__react-alert__ span');
  for (let i = spans.length - 1; i >= 0; i--) {
    const span = spans[i];
    if (span.textContent?.trim() === message && span.parentElement) {
      return span.parentElement;
    }
  }
  return null;
};

const shakeOrderDateAlertElement = (el) => {
  el.classList.remove('order-date-range-alert-shake');
  void el.offsetWidth;
  el.classList.add('order-date-range-alert-shake');
};

const tryShakeOrderDateAlert = (message) => {
  const el = findOrderDateAlertElement(message);
  if (el) {
    cachedOrderDateAlertEl = el;
    shakeOrderDateAlertElement(el);
    return true;
  }
  return false;
};

const clearShakeRetry = () => {
  if (shakeRetryTimeoutId) {
    clearTimeout(shakeRetryTimeoutId);
    shakeRetryTimeoutId = null;
  }
};

const scheduleShakeRetries = (message, attempt, onGiveUp) => {
  clearShakeRetry();
  shakeRetryTimeoutId = setTimeout(() => {
    shakeRetryTimeoutId = null;
    if (lastOrderDateAlertMsg !== message) {
      return;
    }
    if (tryShakeOrderDateAlert(message)) {
      return;
    }
    if (attempt + 1 < SHAKE_MAX_RETRIES) {
      scheduleShakeRetries(message, attempt + 1, onGiveUp);
      return;
    }
    onGiveUp();
  }, SHAKE_RETRY_MS);
};

/**
 * Order date-range alerts: same message shakes existing toast instead of stacking.
 * State is module-level so it works across order tabs and remounts.
 */
export function useOrderDateAlert(alert) {
  const showNewOrderDateAlert = useCallback((message, type = 'info') => {
    clearShakeRetry();

    if (lastOrderDateAlertRef && typeof alert.remove === 'function') {
      alert.remove(lastOrderDateAlertRef);
    }
    lastOrderDateAlertRef = null;
    cachedOrderDateAlertEl = null;

    lastOrderDateAlertMsg = message;

    const onOpen = () => {
      requestAnimationFrame(() => {
        const el = findOrderDateAlertElement(message);
        if (el) {
          cachedOrderDateAlertEl = el;
        }
      });
    };

    const onClose = () => {
      if (lastOrderDateAlertMsg === message) {
        lastOrderDateAlertMsg = null;
      }
      if (lastOrderDateAlertRef?.message === message) {
        lastOrderDateAlertRef = null;
      }
      cachedOrderDateAlertEl = null;
    };

    const options = {
      position: 'top center',
      onOpen,
      onClose,
      [ORDER_DATE_ALERT_FLAG]: true,
    };

    if (type === 'show') {
      lastOrderDateAlertRef = alert.show(message, options);
    } else {
      lastOrderDateAlertRef = alert.info(message, options);
    }
  }, [alert]);

  const showOrderDateAlert = useCallback((message, type = 'info') => {
    const msg = typeof message === 'string' ? message.trim() : message;

    if (lastOrderDateAlertMsg === msg) {
      if (tryShakeOrderDateAlert(msg)) {
        clearShakeRetry();
        return;
      }
      scheduleShakeRetries(msg, 0, () => {
        if (lastOrderDateAlertRef) {
          tryShakeOrderDateAlert(msg);
          return;
        }
        if (lastOrderDateAlertMsg === msg) {
          showNewOrderDateAlert(msg, type);
        }
      });
      return;
    }

    clearShakeRetry();
    showNewOrderDateAlert(msg, type);
  }, [alert, showNewOrderDateAlert]);

  return showOrderDateAlert;
}
